import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar2 } from "iconsax-react";
import { DatePicker, Prompt, Skeleton } from "@/shared/ui";
import { useAppSelector } from "@/shared/hooks";
import { downloadBlob, extractErrorMessage, type Locale } from "@/shared/utils";
import { selectActiveProjectId } from "@/features/settings";
import {
  useDownloadBalanceSheetMutation,
  useDownloadPLReportMutation,
  useDownloadEquityChangesMutation,
} from "../../api";
import { useFinancialReportsPage } from "../../hooks";
import { formatDateRange, formatDateLocal } from "../../utils";
import {
  PLReportCard,
  BalanceSheetCard,
  CashFlowCard,
  ReportsExportDropdown,
  EquityChangesCard,
} from "./components";

export default function FinancialReportsPage() {
  const { t, i18n } = useTranslation("FinancialReportPage");
  const locale = i18n.language as Locale;
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [downloadBalanceSheet, { isLoading: isDownloadingBalance }] = useDownloadBalanceSheetMutation();
  const [downloadPLReport, { isLoading: isDownloadingPL }] = useDownloadPLReportMutation();
  const [downloadEquityChanges, { isLoading: isDownloadingEquityChanges }] = useDownloadEquityChangesMutation();

  const activeProjectId = useAppSelector(selectActiveProjectId);

  const {
    dateRange,
    setDateRange,
    appliedPeriodDates,
    plReport,
    balanceSheet,
    cashFlow,
    equityChanges,
    equityChangesYear,
    isEquityYearSupported,
    isLoadingPLReport,
    isLoadingBalanceSheet,
    isLoadingCashFlow,
    isLoadingEquityChanges,
    isFetchingAny,
    plReportError,
    balanceSheetError,
    cashFlowError,
    equityChangesError,
  } = useFinancialReportsPage({ projectId: activeProjectId });

  const { start_date, end_date } = appliedPeriodDates;
  const isLoadingAny =
    isLoadingPLReport ||
    isLoadingBalanceSheet ||
    isLoadingCashFlow ||
    isLoadingEquityChanges;
  const showReportsSkeleton = isLoadingAny || isFetchingAny;
  const isAnyDownloading = isDownloadingBalance || isDownloadingPL || isDownloadingEquityChanges;

  async function handleDownloadBalanceSheet() {
    if (isAnyDownloading) return;
    try {
      const { blob, filename: serverFilename } = await downloadBalanceSheet(end_date).unwrap();
      const fallbackFilename = `${t("downloadFilename.balanceSheet")}_${end_date}.xlsx`;
      const usedFilename = serverFilename || fallbackFilename;
      downloadBlob(blob, usedFilename);
      setPrompt({
        title: t("messages.downloadSuccess"),
        text: usedFilename,
        variant: "success",
      });
      setTimeout(() => setPrompt(null), 2000);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.downloadFailed"),
        text: extractErrorMessage(err, "messages.downloadFailed", t),
        variant: "error",
      });
    }
  }

  async function handleDownloadEquityChanges() {
    if (isAnyDownloading) return;
    if (equityChangesYear === null || !isEquityYearSupported) return;
    try {
      const { blob, filename: serverFilename } = await downloadEquityChanges({ year: equityChangesYear }).unwrap();
      const fallbackFilename = `${t("downloadFilename.equityChanges")}_${equityChangesYear}.xlsx`;
      const usedFilename = serverFilename || fallbackFilename;
      downloadBlob(blob, usedFilename);
      setPrompt({
        title: t("messages.downloadSuccess"),
        text: usedFilename,
        variant: "success",
      });
      setTimeout(() => setPrompt(null), 2000);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.downloadFailed"),
        text: extractErrorMessage(err, "messages.downloadFailed", t),
        variant: "error",
      });
    }
  }

  async function handleDownloadPLReport() {
    if (isAnyDownloading) return;
    try {
      const { blob, filename: serverFilename } = await downloadPLReport({ start_date, end_date }).unwrap();
      const fallbackFilename = `${t("downloadFilename.plReport")}_${start_date}_${end_date}.xlsx`;
      const usedFilename = serverFilename || fallbackFilename;
      downloadBlob(blob, usedFilename);
      setPrompt({
        title: t("messages.downloadSuccess"),
        text: usedFilename,
        variant: "success",
      });
      setTimeout(() => setPrompt(null), 2000);
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.downloadFailed"),
        text: extractErrorMessage(err, "messages.downloadFailed", t),
        variant: "error",
      });
    }
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <section className="flex-1">
        <div className="flex items-center justify-between pt-1 pb-4 border-b surface-component-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
          <div className="flex items-center gap-2">
            <ReportsExportDropdown
              onDownloadPL={handleDownloadPLReport}
              onDownloadBalance={handleDownloadBalanceSheet}
              onDownloadEquityChanges={handleDownloadEquityChanges}
              isLoading={isAnyDownloading}
            />
            <DatePicker
              mode="range"
              value={dateRange}
              onChange={(v) => {
                if (v) setDateRange(v);
              }}
              locale={locale}
              hasPeriodPicker
              isSmall
              icon={<Calendar2 size={16} color="currentColor" />}
              align="right"
              width="min-w-48"
              formatDisplayValue={(range) => {
                if (!range.start || !range.end) return "";
                return formatDateRange(formatDateLocal(range.start), formatDateLocal(range.end));
              }}
            />
          </div>
        </div>

        {isAnyDownloading && (
          <Prompt
            loaderMode={true}
            onClose={() => {}}
            title=""
            text=""
            loaderText={t("messages.loader.waitingForDownload.text")}
            additionalText={t("messages.loader.waitingForDownload.additionalText")}
            namespace="FinancialReportPage"
          />
        )}

        {prompt && (
          <Prompt
            title={prompt.title}
            text={prompt.text}
            variant={prompt.variant || "success"}
            onClose={() => setPrompt(null)}
            namespace="FinancialReportPage"
          />
        )}

        {showReportsSkeleton ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex flex-col gap-3">
                <div className="py-5 px-6 radius-3xl surface-base-fill">
                  <Skeleton width={200} height={24} />
                  <div className="mt-4 space-y-3">
                    <Skeleton width="100%" height={120} />
                    <Skeleton width="100%" height={120} />
                  </div>
                </div>
                <div className="py-5 px-6 radius-3xl surface-base-fill flex-1">
                  <Skeleton width={200} height={24} />
                  <div className="mt-4 space-y-3">
                    <Skeleton width="100%" height={80} />
                    <Skeleton width="100%" height={150} />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Skeleton width="100%" height={56} borderRadius={8} />
                <Skeleton width="100%" height={56} borderRadius={8} />
                <div className="py-5 px-6 radius-3xl surface-base-fill flex-1">
                  <Skeleton width={200} height={24} />
                  <div className="mt-4 space-y-3">
                    <Skeleton width="100%" height={120} />
                    <Skeleton width="100%" height={120} />
                    <Skeleton width="100%" height={80} />
                  </div>
                </div>
                <div className="py-5 px-6 radius-3xl surface-base-fill">
                  <Skeleton width={240} height={24} />
                  <div className="mt-4 space-y-3">
                    <Skeleton width="100%" height={120} />
                    <Skeleton width="100%" height={60} />
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex flex-col gap-3">
                <PLReportCard data={plReport} isLoading={isFetchingAny} error={plReportError} />
                <CashFlowCard className="flex-1" data={cashFlow} isLoading={isFetchingAny} error={cashFlowError} />
              </div>
              <div className="flex flex-col gap-3">
                <BalanceSheetCard
                  className="flex-1"
                  data={balanceSheet}
                  isLoading={isFetchingAny}
                  error={balanceSheetError}
                  periodDates={appliedPeriodDates}
                />
                <EquityChangesCard
                  data={equityChanges}
                  isLoading={isFetchingAny}
                  error={equityChangesError}
                  activeProjectId={activeProjectId}
                />
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}
